import * as d3 from "d3";
import { memo, PropsWithChildren, useRef, useState } from "react";

import type * as types from "../../../../types/types";
import { ProductionSegmentStyles } from "./ProductionSegmentStyles";
import { mapPnlCategory } from "../../../../utils/pnlMapping";
import {
  ROW_HEIGHT,
  SEGMENT_SEPARATION_SPACE_WIDTH,
  SELECTED_SEGMENT_BORDER_WIDTH,
} from "../../../../constants/constants";
import { camelCase } from "lodash";
import {
  useFocusRange,
  useGetPageFromPath,
  useInterval,
  useTimeAxisContext,
} from "../../../../utils/customHooks/genericHooks";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { useIsTimelineExpanded } from "components/Dashboard/HubletView/SingleTimeline/singleTimelineStore";
import { TAGHIGHLIGHT_EXPANDED_OFFSET_TOP } from "components/Dashboard/HubletView/SingleTimeline/TagHighlightTimelineBackdrop";
import {
  HighlightedTagSegment,
  HighlightedDataSourceSegment,
} from "./ProductionSubSegments";
import { oneSegmentExample } from "utils/storyUtils";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { useAtom } from "jotai";
import { timelineHideSegmentsSmallerThanPxAtom } from "../TimelineCardControls/TimelinesConfig";

interface Props {
  timelineIdx: number;
  segment: types.ProductionSegmentType;
  height: number;
  machemetryId: string;
  showSubSegments?: boolean;
}

const ProductionSegment = memo<Props>(
  ({ timelineIdx, segment, height, machemetryId, showSubSegments = true }) => {
    const { isHubletPage } = useGetPageFromPath();
    const [
      {
        general: { timelineShrinkSegmentCategories: categoriesToShrink },
      },
    ] = useFlightConfig();

    const isStorybook = useIsStorybook();

    const [isTimelineExpanded] = useIsTimelineExpanded();

    const { xLeft, xRight, scaleTime } = useTimeAxisContext();

    const focusRange = useFocusRange();
    const isCurrentTimeVisibleInTimeline =
      focusRange && focusRange.lt > Date.now();
    const isCurrentSegment = !segment.range.lt;
    const now = useUpdatingNow(
      isCurrentSegment && isCurrentTimeVisibleInTimeline
    ); // only update the current time if it's a current segment AND the current time is visible

    // Nulls on the range mean the segment extends forever in either direction
    const x0 = segment.range.ge ? scaleTime(segment.range.ge) || 0 : xLeft;
    const x1 =
      scaleTime(isCurrentSegment ? now : (segment.range.lt as number)) || 0;

    const pathLeft = Math.max(x0, xLeft);
    const pathTop = height / 2;
    const pathRight = Math.min(x1, xRight);

    const areaPath = d3.line()([
      [pathLeft, pathTop],
      [pathRight, pathTop],
    ]);
    /** only show segments if they are wider than 0px */
    const [hideSegmentsSmallerThanPx] = useAtom(
      timelineHideSegmentsSmallerThanPxAtom
    );
    if (!areaPath) {
      return null;
    }
    const minSegmentWidthBeforeHide = hideSegmentsSmallerThanPx;

    const isSmallerThanMinWidth =
      pathRight - pathLeft <= minSegmentWidthBeforeHide;
    // Check if no part of the segment will be displayed
    const isSegmentOutsideOfFocusRange = x0 >= x1 || x0 >= xRight || x1 < xLeft;
    if (isSmallerThanMinWidth || isSegmentOutsideOfFocusRange) {
      return null;
    }

    // TECHDEBT: Handle case where P&L category doesn't exist, but is tagged, differently to make it more obvious/visible in the timeline (possibly a config issue)
    const pnlCategory = segment?.pnl?.category || "unknown";

    const tagName = segment.tag
      ? ` ${camelCase(segment.tag.name)}`
      : pnlCategory === "unknown"
        ? " unknown"
        : pnlCategory === "producing"
          ? " producing"
          : "";

    const mappedPnlCategory = mapPnlCategory(pnlCategory);
    const rectRight = Math.min(x1, xRight);
    const rectLeft = Math.max(x0, xLeft);
    const rectWidth = rectRight - rectLeft;

    if (rectWidth <= 0 || isNaN(rectLeft)) {
      return null;
    }

    return (
      <ProductionSegmentContent
        {...{
          categoriesToShrink,
          isHubletPage,
          isStorybook,
          tagName,
          segment,
          isTimelineExpanded,
          mappedPnlCategory,
          areaPath,
          rectLeft,
          rectWidth,
          now,
          timelineIdx,
          machemetryId,
          showSubSegments,
          rectHeight: height,
        }}
      />
    );
  },
  areEqual
);

// prevents identical segments from re-rendering (e.g. sometimes only the `nonce` is changed in segments.ts > mergeSegments function)
function areEqual(
  prevProps: Readonly<PropsWithChildren<Props>>,
  nextProps: Readonly<PropsWithChildren<Props>>
): boolean {
  const equal =
    prevProps.showSubSegments === nextProps.showSubSegments &&
    prevProps.height === nextProps.height &&
    prevProps.machemetryId === nextProps.machemetryId &&
    prevProps.timelineIdx === nextProps.timelineIdx &&
    isSameSegment(prevProps.segment, nextProps.segment);
  return equal;
}

function isSameSegment(
  prevSegment: types.ProductionSegmentType,
  nextSegment: types.ProductionSegmentType
) {
  return (
    prevSegment.pnl.category === nextSegment.pnl.category &&
    prevSegment.range.ge === nextSegment.range.ge &&
    prevSegment.range.lt === nextSegment.range.lt
  );
}

function ProductionSegmentContent({
  categoriesToShrink,
  isHubletPage,
  isStorybook,
  tagName,
  segment,
  isTimelineExpanded,
  mappedPnlCategory,
  areaPath,
  rectLeft,
  rectWidth,
  now,
  timelineIdx,
  machemetryId,
  showSubSegments,
  rectHeight = ROW_HEIGHT,
}: {
  categoriesToShrink: string[];
  isHubletPage: boolean;
  isStorybook: boolean;
  tagName: string;
  segment: types.ProductionSegmentType;
  isTimelineExpanded: boolean;
  mappedPnlCategory: string;
  areaPath: string;
  rectLeft: number;
  rectWidth: number;
  now: number;
  timelineIdx: number;
  machemetryId: string;
  showSubSegments: boolean;
  rectHeight?: number;
}) {
  const renderCount = useRenderCount();
  const { isTrendsPage } = useGetPageFromPath();
  const randomId = useRandomId();
  const segmentDataset = {
    "data-range-ge": isStorybook ? null : segment.range.ge,
    "data-range-lt": isStorybook ? null : segment.range.lt || now,
    "data-timeline-idx": timelineIdx,
    "data-segment": JSON.stringify(
      isStorybook
        ? { ...oneSegmentExample, range: { ge: null, lt: null } }
        : segment
    ),
    "data-segment-id": randomId,
    "data-machemetry-id": machemetryId,
    "data-mapped-pnl-category": mappedPnlCategory,
    "data-render-count": renderCount,
  };

  let translateY = 0;
  if (isHubletPage) {
    translateY = isTimelineExpanded ? TAGHIGHLIGHT_EXPANDED_OFFSET_TOP : 2.5;
  } else if (isTrendsPage) {
    translateY = 4.5;
  }

  return (
    <ProductionSegmentStyles
      {...{
        $categoriesToShrink: categoriesToShrink,
        $translateY: translateY,
      }}
    >
      {mappedPnlCategory === "loading" ? (
        <path
          d={areaPath || ""}
          className={`production-segment production-segment-loading`}
        />
      ) : (
        <>
          {/* the Production Segent rect */}
          <rect
            id={`production-segment-${randomId}`}
            x={rectLeft}
            y={0}
            height={rectHeight}
            width={rectWidth}
            className={`production-segment production-segment-${mappedPnlCategory} tag-${tagName.trim()}`}
            {...segmentDataset}
          />
          {/* the selected segment indicator */}
          <rect
            // rect for outlining selected segment
            // border goes 1/2 inside, 1/2 outside the rect
            x={rectLeft + SELECTED_SEGMENT_BORDER_WIDTH / 2 - 1}
            y={SELECTED_SEGMENT_BORDER_WIDTH / 2}
            height={rectHeight - SELECTED_SEGMENT_BORDER_WIDTH}
            width={
              rectWidth > SELECTED_SEGMENT_BORDER_WIDTH
                ? rectWidth - SELECTED_SEGMENT_BORDER_WIDTH / 2
                : rectWidth
            }
            className={`production-segment-selected-border production-segment-selected-border-${randomId} hidden ${mappedPnlCategory}`}
            data-timeline-idx={timelineIdx}
          />
          {
            /* if this segment and its previous segment are the same colour,
        show a white space to visually separate them -- avoid doing
           this on small rects */
            segment.isSameCategoryAsPrevSegment &&
              rectWidth > SEGMENT_SEPARATION_SPACE_WIDTH * 2 && (
                <SeparationSpaceRect
                  {...{
                    rectLeft,
                  }}
                />
              )
          }
        </>
      )}
      {showSubSegments && (
        <>
          <HighlightedTagSegment
            {...{
              rectLeft,
              rectWidth,
              segment,
              now,
              segmentDataset,
            }}
          />
          <HighlightedDataSourceSegment
            {...{
              rectLeft,
              rectWidth,
              segment,
              now,
              segmentDataset,
            }}
          />
        </>
      )}
    </ProductionSegmentStyles>
  );
}
/** less memory-intensive version of ProductionSegmentContent used when rendering many segments e.g. focusRange duration >= 3 days */
export function ProductionSegmentContentCheap({
  categoriesToShrink,
  tagName,
  mappedPnlCategory,
  areaPath,
  rectLeft,
  rectWidth,
  segment,
  timelineIdx,
}: {
  categoriesToShrink: string[];
  tagName: string;
  mappedPnlCategory: string;
  areaPath: string;
  rectLeft: number;
  rectWidth: number;
  segment: types.ProductionSegmentType;
  timelineIdx: number;
}) {
  const segmentDataset = {
    "data-range-ge": segment.range.ge,
    "data-range-lt": segment.range.lt,
    "data-timeline-idx": timelineIdx,
    "data-segment": JSON.stringify(segment),
    "data-segment-id": `${segment.range.ge}-${segment.range.lt}-${timelineIdx}-${mappedPnlCategory}`,
    "data-mapped-pnl-category": mappedPnlCategory,
  };
  return (
    <ProductionSegmentStyles
      {...{
        $categoriesToShrink: categoriesToShrink,
        $translateY: 0,
      }}
    >
      {mappedPnlCategory === "loading" ? (
        <path
          d={areaPath || ""}
          className={`production-segment production-segment-${mappedPnlCategory}`}
        />
      ) : (
        <rect
          x={rectLeft}
          y={0}
          height={ROW_HEIGHT}
          width={rectWidth}
          className={`production-segment production-segment-${mappedPnlCategory} tag-${tagName.trim()}`}
          {...segmentDataset}
        />
      )}
    </ProductionSegmentStyles>
  );
}

function SeparationSpaceRect({ rectLeft }) {
  return (
    <rect
      x={rectLeft - SEGMENT_SEPARATION_SPACE_WIDTH / 2} // overlap the separation space across the current segment and prev segment
      y={0}
      height={ROW_HEIGHT}
      width={SEGMENT_SEPARATION_SPACE_WIDTH}
      className={`production-segment-separation-space`}
    />
  );
}

export default ProductionSegment;

/** a current time that updates somewhat faster than the current msPerPixel */
function useUpdatingNow(enable) {
  const [now, setNow] = useState(Date.now());

  const { xLeft, xRight, scaleTime } = useTimeAxisContext();
  const [tLeft, tRight] = scaleTime.domain();
  const msPerPixel = (+tRight - +tLeft) / (xRight - xLeft);

  // Update somewhat faster than whole pixel
  useInterval({
    callback: () => {
      setNow(Date.now());
    },
    delay: enable ? Math.max(100, 0.3 * msPerPixel) : null,
    immediate: false,
  });

  return now;
}

/** count the number of renders (including the first) */
function useRenderCount() {
  const renderCounter = useRef(0);
  renderCounter.current = renderCounter.current + 1;
  return renderCounter.current;
}

function useRandomId() {
  const isStorybook = useIsStorybook();
  const randomId = useRef(Math.round(Math.random() * 10 ** 16)).current;
  return isStorybook ? "storybook" : randomId;
}

/** less memory-intensive version of ProductionSegment used when rendering many segments e.g. focusRange duration >= 3 days */
export const ProductionSegmentCheap = ({
  segment,
  height,
  timelineIdx,
}: {
  segment: types.ProductionSegmentType;
  height: number;
  timelineIdx: number;
}) => {
  const [
    {
      general: { timelineShrinkSegmentCategories: categoriesToShrink },
    },
  ] = useFlightConfig();

  const { xLeft, xRight, scaleTime } = useTimeAxisContext();

  const focusRange = useFocusRange();
  const isCurrentTimeVisibleInTimeline =
    focusRange && focusRange.lt > Date.now();
  const isCurrentSegment = !segment.range.lt;
  const now = useUpdatingNow(
    isCurrentSegment && isCurrentTimeVisibleInTimeline
  ); // only update the current time if it's a current segment AND the current time is visible

  // Nulls on the range mean the segment extends forever in either direction
  const x0 = segment.range.ge ? scaleTime(segment.range.ge) || 0 : xLeft;
  const x1 =
    scaleTime(isCurrentSegment ? now : (segment.range.lt as number)) || 0;

  const pathLeft = Math.max(x0, xLeft);
  const pathTop = height / 2;
  const pathRight = Math.min(x1, xRight);

  const areaPath = d3.line()([
    [pathLeft, pathTop],
    [pathRight, pathTop],
  ]);
  /** only show segments if they are wider than 0px */
  const [hideSegmentsSmallerThanPx] = useAtom(
    timelineHideSegmentsSmallerThanPxAtom
  );
  if (!areaPath) {
    return null;
  }
  const minSegmentWidthBeforeHide = hideSegmentsSmallerThanPx;

  const isSmallerThanMinWidth =
    pathRight - pathLeft <= minSegmentWidthBeforeHide;
  // Check if no part of the segment will be displayed
  const isSegmentOutsideOfFocusRange = x0 >= x1 || x0 >= xRight || x1 < xLeft;
  if (isSmallerThanMinWidth || isSegmentOutsideOfFocusRange) {
    return null;
  }

  // TECHDEBT: Handle case where P&L category doesn't exist, but is tagged, differently to make it more obvious/visible in the timeline (possibly a config issue)
  const pnlCategory = segment?.pnl?.category || "unknown";

  const tagName = segment.tag
    ? ` ${camelCase(segment.tag.name)}`
    : pnlCategory === "unknown"
      ? " unknown"
      : pnlCategory === "producing"
        ? " producing"
        : "";

  const mappedPnlCategory = mapPnlCategory(pnlCategory);
  const rectRight = Math.min(x1, xRight);
  const rectLeft = Math.max(x0, xLeft);
  const rectWidth = rectRight - rectLeft;

  if (rectWidth <= 0 || isNaN(rectLeft)) {
    return null;
  }

  return (
    <ProductionSegmentContentCheap
      {...{
        categoriesToShrink,
        tagName,
        mappedPnlCategory,
        areaPath,
        rectLeft,
        rectWidth,
        segment,
        timelineIdx,
      }}
    />
  );
};
